// frontend/src/App.js

import PrivateRoute from './components/auth/PrivateRoute'; // Asegurate de que ya esté importado arriba
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PermissionGuard } from './components/auth/PermissionGuard';
import { useAuth } from './context/AuthContext';
import UnauthorizedPage from './components/UnauthorizedPage';
import { FullScreenLoader, RouteContentLoader } from './components/ui/PageLoader';

// Layout Components (lazy — solo se cargan dentro de rutas privadas)
const Sidebar = lazy(() => import('./components/Sidebar'));
const Footer = lazy(() => import('./components/Footer'));
const FontSizeControls = lazy(() => import('./components/ui/FontSizeControls').then(m => ({ default: m.FontSizeControls })));

// Pages
const Formulario = lazy(() => import('./pages/Formulario'));
const Tramites = lazy(() => import('./pages/Tramites'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const InformePDF = lazy(() => import('./pages/InformePDF'));
const ImportPage = lazy(() => import('./pages/ImportPage'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const LogViewer = lazy(() => import('./components/admin/LogViewer'));
const SolicitudPublica = lazy(() => import('./pages/SolicitudPublica'));
const ConsultaEstado = lazy(() => import('./pages/ConsultaEstado'));
const AfiliacionPublica = lazy(() => import('./pages/AfiliacionPublica'));
const GestionPublica = lazy(() => import('./pages/GestionPublica'));

const Gracias = lazy(() => import('./pages/Gracias'));

// Auth Components
const LoginForm = lazy(() => import('./components/auth/LoginForm'));
const RegisterForm = lazy(() => import('./components/auth/RegisterForm'));

// Informes Components
const InformeGeneralPDF = lazy(() => import('./components/informes/InformeGeneralPDF'));
const InformeEstadoPDF = lazy(() => import('./components/informes/InformeEstadoPDF'));
const InformeFechaPDF = lazy(() => import('./components/informes/InformeFechaPDF'));
const InformeEstadisticoPDF = lazy(() => import('./components/informes/InformeEstadisticoPDF'));
const InformeDiarioPDF = lazy(() => import('./components/informes/InformeDiarioPDF'));

// Nuevo componente FormularioUnificado
const FormularioUnificado = lazy(() => import('./components/FormularioUnificado'));

// Componentes de Gestiones de Convenios
const GestionesDashboard = lazy(() => import('./components/GestionesDashboard'));
const GestionesForm = lazy(() => import('./components/GestionesForm'));

// Componentes de Gestion de Afiliciones
const AfiliacionesDashboard = lazy(() => import('./components/AfiliacionesDashboard'));
const AfiliacionesForm = lazy(() => import('./components/AfiliacionesForm'));


const routes = [
  { 
    path: '/formulario', 
    element: <PermissionGuard permissions={['create']}><Formulario /></PermissionGuard> 
  },
  { 
    path: '/tramites', 
    element: <PermissionGuard permissions={['read']}><Tramites /></PermissionGuard> 
  },
  { 
    path: '/dashboard', 
    element: <PermissionGuard permissions={['read']}><Dashboard /></PermissionGuard> 
  },
  { 
    path: '/importar', 
    element: <PermissionGuard permissions={['create']}><ImportPage /></PermissionGuard> 
  },
  { 
    path: '/informes', 
    element: <PermissionGuard permissions={['view_reports']}><InformePDF /></PermissionGuard> 
  },
  { 
    path: '/informes/general', 
    element: <PermissionGuard permissions={['view_reports']}><InformeGeneralPDF /></PermissionGuard> 
  },
  { 
    path: '/informes/estado', 
    element: <PermissionGuard permissions={['view_reports']}><InformeEstadoPDF /></PermissionGuard> 
  },
  { 
    path: '/informes/fecha', 
    element: <PermissionGuard permissions={['view_reports']}><InformeFechaPDF /></PermissionGuard> 
  },
  {
    path: '/informes/estadistico',
    element: <PermissionGuard permissions={['view_reports']}><InformeEstadisticoPDF /></PermissionGuard>
  },
  {
    path: '/informes/diario',
    element: <PermissionGuard permissions={['view_reports']}><InformeDiarioPDF /></PermissionGuard>
  },
  {
    path: '/admin/users',
    element: <PermissionGuard permissions={['manage_users']}><AdminUsers /></PermissionGuard>
  },
  {
    path: '/admin/logs',
    element: <PermissionGuard permissions={['manage_users']}><LogViewer /></PermissionGuard>
  },
  {
    path: '/solicitud-publica',
    element: <PermissionGuard permissions={['read']}><SolicitudPublica /></PermissionGuard>
  },
  {
    path: '/solicitud-publica/editar/:id',
    element: <PermissionGuard permissions={['update']}><SolicitudPublica /></PermissionGuard>
  },
  // Nueva ruta para el formulario unificado
  {
    path: '/formulario-unificado',
    element: (
      <PrivateRoute requiredRoles={["administrador", "auditor_interior", "auditor_capital", "auditor_bsas", "auditor_general", "auditor_medico_general", "solo_derivaciones", "derivaciones_y_convenios", "derivaciones_interior", "operador"]}>
        <FormularioUnificado />
      </PrivateRoute>
    )
  },
  
  // Nuevas rutas para Gestiones de Convenios
  {
    path: '/gestiones',
    element: <PermissionGuard permissions={['read']}><GestionesDashboard /></PermissionGuard>
  },
  {
    path: '/gestiones/nuevo',
    element: <PermissionGuard permissions={['create']}><GestionesForm /></PermissionGuard>
  },
  {
    path: '/gestiones/editar',
    element: <PermissionGuard permissions={['update']}><GestionesForm /></PermissionGuard>
  },

   // Nuevas rutas para Gestion de afiliados

   {
    path: '/afiliaciones',
    element: <PermissionGuard permissions={['read']}><AfiliacionesDashboard /></PermissionGuard>
  },
  {
    path: '/afiliaciones/nuevo',
    element: <PermissionGuard permissions={['create']}><AfiliacionesForm /></PermissionGuard>
  },
  {
    path: '/afiliaciones/editar',
    element: <PermissionGuard permissions={['update']}><AfiliacionesForm /></PermissionGuard>
  },


];

const publicRoutes = [
  {
    path: '/solicitud',
    element: <SolicitudPublica />
  },
  {
    path: '/consulta-estado',
    element: <ConsultaEstado />
  },
  {
    path: '/gestion-publica',
    element: <GestionPublica />
  },
  {
    path: '/afiliacion',
    element: <AfiliacionPublica />
  },
  {
    path: '/gracias',
    element: <Gracias />
  }
];

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenLoader label="Validando acceso..." />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const PrivateRoutes = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenLoader label="Cargando tu espacio de trabajo..." />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  useEffect(() => {
    // Valor inicial del ancho del sidebar (ajustá si tu sidebar mide otro ancho)
    document.documentElement.style.setProperty('--sidebar-width', '250px');

    // Preload del Dashboard: arranca la descarga del chunk aunque
    // todavía no se navegó — se descarga en paralelo con auth.
    import('./pages/Dashboard').catch(() => {});
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Routes>
          {publicRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}

          <Route path="/login" element={
            <PublicRoute>
              <Suspense fallback={<FullScreenLoader label="Preparando ingreso..." />}>
                <LoginForm />
              </Suspense>
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <Suspense fallback={<FullScreenLoader label="Preparando registro..." />}>
                <RegisterForm />
              </Suspense>
            </PublicRoute>
          } />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          <Route
            path="/*"
            element={
              <PrivateRoutes>
                <div className="flex flex-col min-h-screen bg-custom-bgapos">
                  <div className="flex flex-1">
                    <Suspense fallback={null}>
                      <Sidebar />
                    </Suspense>
                    <div className="flex-1 p-4 overflow-auto pb-6">
                      <Suspense fallback={<RouteContentLoader />}>
                        <Routes>
                          {routes.map(({ path, element }) => (
                            <Route key={path} path={path} element={element} />
                          ))}
                          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </Suspense>
                    </div>
                  </div>
                  <Suspense fallback={null}>
                    <Footer />
                  </Suspense>
                  <Suspense fallback={null}>
                    <FontSizeControls />
                  </Suspense>
                </div>
              </PrivateRoutes>
            }
          />
        </Routes>
      </Router>
      
       {/* 🔔 Contenedor de notificaciones */}
       <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
    </AuthProvider>
    
  );
}

export default App;
